import { Accordion } from '@/components/basicComponents/accordion';
import DatePicker from '@/components/basicComponents/datePicker';
import Input from '@/components/basicComponents/input';
import { formatPercentageInputValue, handleCursorPositionInPercentageInput } from '@/components/basicComponents/input/utils';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Textarea from '@/components/basicComponents/textarea';
import { AddButton } from '@/components/dealPipeline-page/modify/addButton';
import { DeleteDynamicItem, DynamicSelectsWrapper, DynamicSelectWrapper } from '@/components/dealPipeline-page/modify/radioWithDynamicSelects';
import { IListDataWithId } from '@/components/dealPipeline-page/modify/types';
import {
  addDynamicSelect,
  deleteDynamicSelect,
  getDynamicSelectPreviewValue,
  onChangeDynamicSelect
} from '@/components/dealPipeline-page/modify/utils';
import Label from '@/components/basicComponents/label';
import { ThreeColumnWrapper, TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useResponsive } from '@/hooks/use-responsive';
import { Dispatch, SetStateAction } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { IDiligenceForm } from '../..';

interface DetailsSectionProps {
  control: Control<IDiligenceForm, any, IDiligenceForm>;
  tags: IListDataWithId;
  strategies: IListDataWithId;
  managerList: string[];
  ownerSelectOptions: ISelectValue[];
  funnelSelectOptions: ISelectValue[];
  setTags: Dispatch<SetStateAction<IListDataWithId>>;
  setStrategies: Dispatch<SetStateAction<IListDataWithId>>;
  openModalWindow: (type: string) => void;
}

export const DetailsSection = ({
  control,
  tags,
  strategies,
  managerList,
  ownerSelectOptions,
  funnelSelectOptions,
  setTags,
  setStrategies,
  openModalWindow
}: DetailsSectionProps) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation('enums');
  const [searchParams] = useSearchParams();

  const recordId = searchParams.get('recordId') || '';

  return (
    <Accordion title="Details">
      <TwoColumnWrapper isMobile={isMobile}>
        <Controller
          name="name"
          control={control}
          rules={{
            required: {
              value: true,
              message: 'Name is required'
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              value={value || ''}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              label="Name"
              placeholder="Friendly name for use internally and with clients"
              errorText={error?.message}
              size="md"
              styleType="form"
              isRequiredField
            />
          )}
        />
        <Controller
          name="assetClass"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              value={value || ''}
              setValue={onChange}
              options={[
                { value: '', label: 'Select' },
                createSimpleSelectOption(t(`enums:AssetsClass.PRIVATE_REAL_ASSETS`)),
                createSimpleSelectOption(t(`enums:AssetsClass.PRIVATE_CREDIT`)),
                createSimpleSelectOption(t(`enums:AssetsClass.PRIVATE_EQUITY`))
              ]}
              withSearch
              label="Asset Class"
              errorText={error?.message}
              size="md"
              styleType="form"
            />
          )}
        />
      </TwoColumnWrapper>
      <TwoColumnWrapper isMobile={isMobile}>
        <div>
          <Controller
            name="managerId"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                value={value || ''}
                setValue={onChange}
                options={[{ value: '', label: 'Select' }, ...managerList.map((manager) => createSimpleSelectOption(manager))]}
                withSearch
                label="Manager"
                errorText={error?.message}
                size="md"
                styleType="form"
              />
            )}
          />
          <AddButton text="Create New" onClick={() => openModalWindow('create-manager')} />
        </div>
        <Controller
          name="finalClose"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DatePicker
              label="Close Date"
              value={value || ''}
              onChange={(date) => {
                onChange(date || '');
              }}
              tooltip={'The date the investment period is expected to end.'}
              errorText={error?.message}
              size="md"
              styleType="form"
              ignoreLocalization={true}
            />
          )}
        />
      </TwoColumnWrapper>
      <TwoColumnWrapper isMobile={isMobile}>
        <DynamicSelectsWrapper>
          <Label size="md" tooltip="Used to categorize investments and useful for portfolio construction.">
            Strategies
          </Label>
          {strategies.selected.map((el, index) => (
            <DynamicSelectWrapper key={`strategies-${index}`} loading={index === strategies.selected.length - 1 && strategies.duplicate ? 1 : 0}>
              <Select
                value={strategies.selected[index]}
                previewValue={getDynamicSelectPreviewValue(strategies.selected[index], strategies.allList)}
                setValue={(value) => onChangeDynamicSelect(value, index, strategies, setStrategies)}
                options={strategies.list}
                styleType={
                  strategies.selected[strategies.selected.length - 1] !== el ||
                  strategies.selected[strategies.selected.length - 1] === strategies.duplicate
                    ? 'disabled'
                    : 'form'
                }
                size="md"
                withSearch
              />
              <DeleteDynamicItem onClick={() => deleteDynamicSelect(index, strategies, setStrategies)}>
                <img src="/static/icons/blue-cross.svg" alt="cross" />
              </DeleteDynamicItem>
            </DynamicSelectWrapper>
          ))}
          {strategies.list.length && strategies.selected[strategies.selected.length - 1] !== '' ? (
            <AddButton text="Add" onClick={() => addDynamicSelect(setStrategies)} />
          ) : (
            <AddButton text="Create New" onClick={() => openModalWindow('create-strategy')} />
          )}
        </DynamicSelectsWrapper>

        <DynamicSelectsWrapper>
          <Label size="md" tooltip="Call out fund structure, ESG focus, or any other common attributes across different funds.">
            Tags
          </Label>
          {tags.selected.map((el, index) => (
            <DynamicSelectWrapper key={`strategies-${index}`} loading={index === tags.selected.length - 1 && tags.duplicate ? 1 : 0}>
              <Select
                value={tags.selected[index]}
                previewValue={getDynamicSelectPreviewValue(tags.selected[index], tags.allList)}
                setValue={(value) => onChangeDynamicSelect(value, index, tags, setTags)}
                options={tags.list}
                styleType={
                  tags.selected[tags.selected.length - 1] !== el || tags.selected[tags.selected.length - 1] === tags.duplicate ? 'disabled' : 'form'
                }
                size="md"
                withSearch
              />
              <DeleteDynamicItem onClick={() => deleteDynamicSelect(index, tags, setTags)}>
                <img src="/static/icons/blue-cross.svg" alt="cross" />
              </DeleteDynamicItem>
            </DynamicSelectWrapper>
          ))}
          {tags.list.length && tags.selected[tags.selected.length - 1] !== '' ? (
            <AddButton text="Add" onClick={() => addDynamicSelect(setTags)} />
          ) : (
            <AddButton text="Create New" onClick={() => openModalWindow('create-tag')} />
          )}
        </DynamicSelectsWrapper>
      </TwoColumnWrapper>
      <ThreeColumnWrapper isMobile={isMobile}>
        <Controller
          name="targetIRR"
          control={control}
          rules={{
            pattern: {
              value: /^(\d+(?:[\.\,]\d{1,2})?%?)$/,
              message: 'Only numbers are allowed'
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              value={value || ''}
              onChange={(e) => {
                const formattedValue = formatPercentageInputValue(e);
                onChange(formattedValue);
              }}
              onClick={handleCursorPositionInPercentageInput}
              onKeyUp={handleCursorPositionInPercentageInput}
              label="Target IRR"
              placeholder="0%"
              errorText={error?.message}
              size="md"
              styleType="form"
            />
          )}
        />
        <Controller
          name="targetYield"
          control={control}
          rules={{
            pattern: {
              value: /^(\d+(?:[\.\,]\d{1,2})?%?)$/,
              message: 'Only numbers are allowed'
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              value={value || ''}
              onChange={(e) => {
                const formattedValue = formatPercentageInputValue(e);
                onChange(formattedValue);
              }}
              onClick={handleCursorPositionInPercentageInput}
              onKeyUp={handleCursorPositionInPercentageInput}
              label="Target Yield"
              placeholder="0%"
              errorText={error?.message}
              size="md"
              styleType="form"
            />
          )}
        />
        <Controller
          name="vintageYear"
          control={control}
          rules={{
            pattern: {
              value: /^\d{4}$/,
              message: 'Enter a year using four digits'
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              value={value || ''}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              label="Vintage"
              placeholder="Enter a year using four digits"
              errorText={error?.message}
              tooltip={'For the purposes of portfolio diversification.'}
              size="md"
              styleType="form"
            />
          )}
        />
      </ThreeColumnWrapper>
      <TwoColumnWrapper isMobile={isMobile}>
        <Controller
          name="funnelId"
          control={control}
          rules={{
            required: {
              value: true,
              message: 'Funnel is required'
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              value={value || ''}
              setValue={onChange}
              options={funnelSelectOptions}
              withSearch
              label="Funnel"
              errorText={error?.message}
              size="md"
              styleType={recordId ? 'disabled' : 'form'}
              isRequiredField
            />
          )}
        />
        <Controller
          name="ownerEmail"
          control={control}
          rules={{
            required: {
              value: true,
              message: 'Due Diligence Owner is required'
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              value={value || ''}
              setValue={onChange}
              options={ownerSelectOptions}
              withSearch
              label="Due Diligence Owner"
              errorText={error?.message}
              size="md"
              styleType="form"
              isRequiredField
            />
          )}
        />
      </TwoColumnWrapper>
      <TwoColumnWrapper isMobile={isMobile}>
        <Controller
          name="contact"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              value={value || ''}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              label="Contact"
              placeholder="Representative name"
              errorText={error?.message}
              size="md"
              styleType="form"
            />
          )}
        />
        <Controller
          name="referral"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              value={value || ''}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              label="Referral"
              placeholder="Name of individual who referred you to the opportunity"
              errorText={error?.message}
              size="md"
              styleType="form"
            />
          )}
        />
      </TwoColumnWrapper>
      <Controller
        name="description"
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Textarea
            value={value || ''}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            limitCharactersTo={250}
            label="Description"
            tooltip="This appears on content generated for your internal team or investors."
            size="md"
            styleType="form"
          />
        )}
      />
    </Accordion>
  );
};
